import { useState, useEffect } from 'react';
import API from '../utils/api';

/**
 * ProductPicker — زر اختيار منتج موحد لكل الصفحات
 * Props:
 *   value: product_id المختار
 *   onChange: (product) => void
 *   placeholder: نص الزر
 *   disabled: boolean
 */
export default function ProductPicker({ value, onChange, placeholder = 'اختر منتج', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [fCat, setFCat] = useState('');
  const [fBrand, setFBrand] = useState('');
  const [fSize, setFSize] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    API.get('/products').then(r => setProducts(r.data));
  }, []);

  useEffect(() => {
    if (value && products.length) {
      setSelected(products.find(p => p.id === +value) || null);
    } else if (!value) {
      setSelected(null);
    }
  }, [value, products]);

  const categories = [...new Set(products.map(p => p.category_name).filter(Boolean))].sort();
  const brands = [...new Set(
    products
      .filter(p => !fCat || p.category_name === fCat)
      .map(p => p.brand)
      .filter(b => b && b !== '-')
  )].sort();
  const sizes = [...new Set(
    products
      .filter(p => (!fCat || p.category_name === fCat) && (!fBrand || p.brand === fBrand))
      .map(p => p.size)
      .filter(Boolean)
  )].sort();

  const filtered = products.filter(p => {
    const matchCat = !fCat || p.category_name === fCat;
    const matchBrand = !fBrand || p.brand === fBrand;
    const matchSize = !fSize || p.size === fSize;
    const matchSearch = !search || p.name.includes(search) || (p.brand||'').includes(search);
    return matchCat && matchBrand && matchSize && matchSearch;
  });

  const pick = (product) => {
    setSelected(product);
    onChange(product);
    setOpen(false);
    setFCat(''); setFBrand(''); setFSize(''); setSearch('');
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onChange(null);
  };

  return (
    <>
      {/* الزر */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 8,
          border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
          background: selected ? 'var(--primary-light)' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, fontFamily: 'inherit', fontSize: '0.875rem', textAlign: 'right',
          transition: 'all 0.15s',
        }}
      >
        {selected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <span style={{
              background: 'var(--primary)', color: 'white',
              borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', whiteSpace: 'nowrap'
            }}>{selected.category_name || 'عام'}</span>
            <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.name}
            </span>
            {selected.brand && selected.brand !== '-' && (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{selected.brand}</span>
            )}
            {selected.size && (
              <span style={{
                background: '#e5e7eb', borderRadius: 6, padding: '1px 7px',
                fontSize: '0.75rem', whiteSpace: 'nowrap'
              }}>{selected.size}</span>
            )}
          </div>
        ) : (
          <span style={{ color: '#9ca3af' }}>🔍 {placeholder}</span>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {selected && (
            <span onClick={clear} style={{
              color: 'var(--danger)', fontWeight: 700, fontSize: '1rem',
              padding: '0 4px', lineHeight: 1,
            }}>✕</span>
          )}
          <span style={{ color: 'var(--primary)', fontSize: '0.8rem' }}>▼</span>
        </div>
      </button>

      {/* Modal */}
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">🔍 اختيار منتج</span>
              <button className="modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {/* فلاتر */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                <input
                  placeholder="🔍 بحث..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
                <select value={fCat} onChange={e => { setFCat(e.target.value); setFBrand(''); setFSize(''); }}>
                  <option value="">كل التصنيفات</option>
                  {categories.map(c => <option key={c}>{c}</option>)}
                </select>
                <select value={fBrand} onChange={e => { setFBrand(e.target.value); setFSize(''); }}>
                  <option value="">كل البراندات</option>
                  {brands.map(b => <option key={b}>{b}</option>)}
                </select>
                <select value={fSize} onChange={e => setFSize(e.target.value)}>
                  <option value="">كل الأحجام</option>
                  {sizes.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>

              {/* جدول النتائج */}
              <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ margin: 0 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th>التصنيف</th>
                      <th>اسم المنتج</th>
                      <th>البراند</th>
                      <th>الحجم</th>
                      <th>الوحدة</th>
                      <th>المتوفر</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.id}
                        onClick={() => pick(p)}
                        style={{
                          cursor: 'pointer',
                          background: selected?.id === p.id ? 'var(--primary-light)' : undefined,
                        }}
                      >
                        <td>
                          <span style={{
                            background: 'var(--info-light)', color: 'var(--info)',
                            borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', whiteSpace: 'nowrap'
                          }}>{p.category_name || 'عام'}</span>
                        </td>
                        <td className="font-bold">{p.name}</td>
                        <td className="text-muted">{p.brand && p.brand !== '-' ? p.brand : '-'}</td>
                        <td>
                          {p.size ? (
                            <span style={{
                              background: '#f3f4f6', borderRadius: 6,
                              padding: '2px 8px', fontSize: '0.8rem'
                            }}>{p.size}</span>
                          ) : '-'}
                        </td>
                        <td className="text-muted">{p.unit}</td>
                        <td>
                          <span className={`badge ${p.current_qty > (p.min_stock||0) ? 'badge-success' : 'badge-danger'}`}>
                            {p.current_qty}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-primary" onClick={() => pick(p)}>
                            اختر
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={7}>
                        <div className="empty-state" style={{ padding: 24 }}>
                          <div className="empty-state-icon">🔍</div>
                          <p>لا توجد نتائج</p>
                        </div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                {filtered.length} منتج من أصل {products.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
